/**
 * PRDViewer Component
 *
 * Display a complete PRD document with collapsible sections
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  FileText,
  Download,
  AlertCircle,
  CheckCircle2,
  Clock,
  Archive,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { sanitizeMarkdownHtml } from '@/lib/sanitize';
import { usePRD } from '@/hooks/usePRD';
import type { PRDStatus, PRDPriority } from '@forgenetwork/types';

interface PRDViewerProps {
  prdId: string;
  onExport?: () => void;
}

/**
 * Status badge variants and icons
 */
const statusConfig: Record<
  PRDStatus,
  { variant: 'default' | 'secondary' | 'destructive'; icon: React.ComponentType<{ className?: string }> }
> = {
  draft: { variant: 'secondary', icon: Clock },
  published: { variant: 'default', icon: CheckCircle2 },
  archived: { variant: 'destructive', icon: Archive },
};

/**
 * Priority badge colors
 */
const priorityColors: Record<PRDPriority, string> = {
  P0: 'bg-red-100 text-red-800 border-red-300',
  P1: 'bg-orange-100 text-orange-800 border-orange-300',
  P2: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  P3: 'bg-blue-100 text-blue-800 border-blue-300',
};

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * PRDViewer - Display a complete PRD document
 */
export function PRDViewer({ prdId, onExport }: PRDViewerProps): React.JSX.Element {
  const { data, isLoading, error } = usePRD(prdId);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-32 bg-muted animate-pulse rounded-xl" />
        <div className="h-64 bg-muted animate-pulse rounded-xl" />
        <div className="h-96 bg-muted animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>{error instanceof Error ? error.message : 'Failed to load PRD'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data?.prd) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">PRD not found</p>
        </CardContent>
      </Card>
    );
  }

  const prd = data.prd;
  const StatusIcon = statusConfig[prd.status].icon;

  return (
    <div className="space-y-6 print:space-y-4">
      {/* Header */}
      <Card className="print:shadow-none">
        <CardHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <FileText className="h-6 w-6 text-primary" />
                <CardTitle className="text-3xl">{prd.title}</CardTitle>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <Badge variant={statusConfig[prd.status].variant} className="gap-1">
                  <StatusIcon className="h-3 w-3" />
                  {prd.status.toUpperCase()}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Version {prd.version}
                </span>
                <span className="text-sm text-muted-foreground">
                  Generated {formatDate(prd.generatedAt)}
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={onExport}
              className="gap-2 print:hidden"
            >
              <Download className="h-4 w-4" />
              Export
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Sections */}
      <Card className="print:shadow-none">
        <CardContent className="pt-6">
          <Accordion type="multiple" defaultValue={prd.sections.map((s) => s.id)}>
            {prd.sections
              .sort((a, b) => a.order - b.order)
              .map((section) => (
                <AccordionItem key={section.id} value={section.id}>
                  <AccordionTrigger className="text-lg font-semibold">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="prose prose-sm max-w-none">
                      {/* SECURITY: Sanitize HTML to prevent XSS from backend content */}
                      <div
                        className="whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: sanitizeMarkdownHtml(section.content) }}
                      />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Features */}
      {prd.features && prd.features.length > 0 && (
        <Card className="print:shadow-none print:break-inside-avoid">
          <CardHeader>
            <CardTitle>Features</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {prd.features.map((feature) => (
                <div
                  key={feature.id}
                  className="border rounded-lg p-4 space-y-2"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-semibold">{feature.name}</h4>
                    <Badge
                      className={cn('shrink-0', priorityColors[feature.priority])}
                    >
                      {feature.priority}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                  {feature.category && (
                    <Badge variant="outline" className="text-xs">
                      {feature.category}
                    </Badge>
                  )}
                  {feature.effort && (
                    <span className="text-xs text-muted-foreground">
                      Effort: {feature.effort}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Goals */}
      {prd.goals && prd.goals.length > 0 && (
        <Card className="print:shadow-none print:break-inside-avoid">
          <CardHeader>
            <CardTitle>Goals</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-2">
              {prd.goals
                .sort((a, b) => a.order - b.order)
                .map((goal) => (
                  <li key={goal.id} className="text-sm">
                    {goal.description}
                  </li>
                ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Success Metrics */}
      {prd.metrics && prd.metrics.length > 0 && (
        <Card className="print:shadow-none print:break-inside-avoid">
          <CardHeader>
            <CardTitle>Success Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {prd.metrics
                .sort((a, b) => a.order - b.order)
                .map((metric) => (
                  <div key={metric.id} className="border-l-2 border-primary pl-4">
                    <h4 className="font-medium text-sm">{metric.name}</h4>
                    <p className="text-sm text-muted-foreground">
                      {metric.description}
                    </p>
                    {metric.target && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Target: {metric.target}
                      </p>
                    )}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dependencies */}
      {prd.dependencies && prd.dependencies.length > 0 && (
        <Card className="print:shadow-none print:break-inside-avoid">
          <CardHeader>
            <CardTitle>Dependencies</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {prd.dependencies.map((dep) => (
                <div
                  key={dep.id}
                  className={cn(
                    'border rounded-lg p-3',
                    dep.critical && 'border-destructive bg-destructive/5'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-medium text-sm">{dep.name}</h4>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {dep.type}
                      </Badge>
                      {dep.critical && (
                        <Badge variant="destructive" className="text-xs">
                          Critical
                        </Badge>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {dep.description}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
